import { useMemo } from "react"
import type {
  DataGridProProps,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro"
import { useUrlFilters, type UrlFilterSchema } from "@core/hooks/useUrlFilters"
import type {
  PageQuery,
  PublicationsQuery,
  TemplatesQuery,
} from "../interface/publications"
import type { PlacementsQuery } from "../interface/schedule"
import type { MediaQuery } from "../interface/media"
import equal from "fast-deep-equal"

interface Queries {
  templates: TemplatesQuery
  versions: PageQuery
  publications: PublicationsQuery
  placements: PlacementsQuery
  history: PageQuery
  media: MediaQuery
}
type Resource = keyof Queries
type Filters<R extends Resource> = Omit<Queries[R], keyof PageQuery>
const allowed: Record<Resource, readonly string[]> = {
  templates: ["search"],
  versions: [],
  publications: ["templateId", "templateVersionId"],
  placements: ["publicationId", "channelId", "enabled", "status"],
  history: [],
  media: ["kind", "status"],
}
const pageValue = (value: unknown): number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value < Number.MAX_SAFE_INTEGER
    ? value
    : 0
const limitValue = (value: unknown): number =>
  typeof value === "number" && Number.isInteger(value) && value > 0
    ? Math.min(value, 200)
    : 50

function filterValue(
  resource: Resource,
  field: string,
  value: unknown,
): string | boolean | undefined {
  if (value === undefined || value === "") return undefined
  if (field === "enabled")
    return value === true || value === "true"
      ? true
      : value === false || value === "false"
        ? false
        : undefined
  if (typeof value !== "string") return undefined
  if (field === "kind" && !["photo", "video"].includes(value)) return undefined
  if (
    field === "status" &&
    !(
      resource === "media" ? ["ready", "deleting"] : ["live", "deleted", "all"]
    ).includes(value)
  )
    return undefined
  return value
}

/** Namespaced URL UI page (zero-based) + limit; API always gets page/limit only.
 * Parent route IDs (e.g. version templateId/history id) are supplied by the caller.
 * No global pagination/sort behavior is changed. Column sorting is disabled.
 */
export function usePostBotListState<R extends Resource>({
  resource,
  key = resource,
}: {
  resource: R
  key?: string
}) {
  const schema = useMemo(
    () =>
      Object.fromEntries([
        [`${key}.page`, "number"],
        [`${key}.limit`, "number"],
        ...allowed[resource].map((field) => [`${key}.${field}`, "string"]),
      ]) as UrlFilterSchema,
    [key, resource],
  )
  const { read, write } = useUrlFilters(schema)
  const raw = read()
  const page = pageValue(raw[`${key}.page`]),
    limit = limitValue(raw[`${key}.limit`])
  const filters = Object.fromEntries(
    allowed[resource].flatMap((field) => {
      const value = filterValue(resource, field, raw[`${key}.${field}`])
      return value === undefined ? [] : [[field, value]]
    }),
  ) as Filters<R>
  const writeState = (
    nextPage: number,
    nextLimit: number,
    nextFilters: Filters<R>,
  ) => {
    write({
      [`${key}.page`]: nextPage === 0 ? undefined : nextPage,
      [`${key}.limit`]: nextLimit === 50 ? undefined : nextLimit,
      ...Object.fromEntries(
        Object.entries(nextFilters).map(([field, value]) => [
          `${key}.${field}`,
          String(value),
        ]),
      ),
    })
  }
  const setPaginationModel = (model: GridPaginationModel) => {
    const nextLimit = limitValue(model.pageSize)
    writeState(
      nextLimit === limit ? pageValue(model.page) : 0,
      nextLimit,
      filters,
    )
  }
  /** Partial update; undefined clears a filter. Unknown keys never reach URL/API. */
  const setFilters = (values: Partial<Filters<R>>) => {
    const combined = { ...filters, ...values } as Record<string, unknown>
    const next = Object.fromEntries(
      allowed[resource].flatMap((field) => {
        const value = filterValue(resource, field, combined[field])
        return value === undefined ? [] : [[field, value]]
      }),
    ) as Filters<R>
    if (!equal(next, filters)) writeState(0, limit, next)
  }
  const paginationModel = { page, pageSize: limit }
  return {
    page,
    limit,
    filters,
    paginationModel,
    setPaginationModel,
    setFilters,
    listArgs: { ...filters, page: page + 1, limit } as Queries[R] & {
      page: number
      limit: number
    },
    gridProps: {
      pagination: true as const,
      paginationMode: "server" as const,
      paginationModel,
      onPaginationModelChange: setPaginationModel,
      pageSizeOptions: [25, 50, 100, 200],
      sortingMode: "server" as const,
      sortModel: [] as GridSortModel,
      disableColumnMenu: true,
      sortingOrder: [],
    } satisfies Partial<DataGridProProps>,
  }
}
